import { ReviewAuditLog, ReviewKind, ReviewUser } from "api/admin/kycReviews";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import Spinner from "components/ui/Spinner";
import { currentAdminCountryScope } from "helpers/storage";
import { DateTime } from "luxon";
import { FormEvent, ReactNode, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminRoutes } from "routes/routes";

export const reviewPath = (kind: ReviewKind) =>
  kind === "edd" ? adminRoutes.eddReviews : adminRoutes.kybReviews;
export const reviewTitle = (kind: ReviewKind) =>
  kind === "edd"
    ? "Tier 3 · Enhanced due diligence"
    : "Tier 4 · Business verification";
export const reviewKey = (kind: ReviewKind, adminID?: number) => [
  "kycReviews",
  kind,
  adminID,
  currentAdminCountryScope(),
];
export const userLabel = (user: ReviewUser | null, userID: number) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
  user?.userName ||
  `User #${userID}`;
export const dateLabel = (value: string | null) => {
  const date = value ? DateTime.fromISO(value) : null;
  return date?.isValid ? date.toFormat("dd LLL yyyy, HH:mm ZZZZ") : "—";
};
export const humanize = (value: string) =>
  value.replace(/_/g, " ").replace(/^[a-z]/, (letter) => letter.toUpperCase());
export const reviewError = (error: unknown) => {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === "string" && message
    ? message
    : "Unable to complete this request. Please try again.";
};

const statusLabels: Record<string, string> = {
  submitted: "Under review",
  pending: "Under review",
  approved: "Approved",
  completed: "Verified",
  declined: "Rejected",
  failed: "Rejected",
  not_verified: "Not submitted",
};
export function StatusBadge({ status }: { status: string }) {
  const color = ["completed", "approved"].includes(status)
    ? "bg-green-100 text-green-800"
    : ["failed", "declined"].includes(status)
      ? "bg-red-100 text-red-800"
      : ["pending", "submitted"].includes(status)
        ? "bg-yellow-100 text-yellow-800"
        : "bg-gray-100 text-gray-700";
  return (
    <span
      className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${color}`}
    >
      {statusLabels[status] ?? humanize(status)}
    </span>
  );
}

export function ReviewLayout({
  kind,
  children,
  detail = false,
}: {
  kind: ReviewKind;
  children: ReactNode;
  detail?: boolean;
}) {
  return (
    <Container className="flex-col md:flex-row">
      <AdminSidebar />
      <Main className="w-full min-w-0 pb-12 text-left">
        {detail && (
          <Link
            className="inline-block mb-5 text-sm underline"
            to={reviewPath(kind)}
          >
            Back to {kind === "edd" ? "Tier 3" : "Tier 4"} reviews
          </Link>
        )}
        <p className="mb-2 text-sm text-gray-500">KYC reviews</p>
        <h1 className="mb-3 text-2xl font-medium">{reviewTitle(kind)}</h1>
        <p className="mb-8 text-sm text-gray-600">
          {kind === "edd"
            ? "Review the customer's financial profile and trading activity before approving Tier 3."
            : "Review business details, documents, and beneficial owners. Tier 4 is granted when all three sections are approved."}
        </p>
        {children}
      </Main>
    </Container>
  );
}

export function ReviewLoading() {
  return (
    <div role="status" className="flex items-center gap-3 py-12">
      <Spinner /> Loading review…
    </div>
  );
}

export function ReviewError({
  error,
  retry,
}: {
  error: unknown;
  retry: () => void;
}) {
  return (
    <div role="alert" className="p-5 border border-red-200 rounded bg-red-50">
      <p>{reviewError(error)}</p>
      <button type="button" onClick={retry} className="mt-3 underline">
        Try again
      </button>
    </div>
  );
}

export function ReviewSection({
  title,
  status,
  children,
}: {
  title: string;
  status?: string;
  children: ReactNode;
}) {
  return (
    <section className="p-5 mb-6 bg-white border border-gray-200 rounded-lg">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-lg font-medium">{title}</h2>
        {status && <StatusBadge status={status} />}
      </div>
      {children}
    </section>
  );
}

export function ReviewFields({ fields }: { fields: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      {fields.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="mb-1 text-sm text-gray-500">{label}</dt>
          <dd className="text-sm break-words">{value || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Applicant({
  user,
  userID,
}: {
  user: ReviewUser | null;
  userID: number;
}) {
  return (
    <ReviewSection title={userLabel(user, userID)}>
      <ReviewFields
        fields={[
          ["User ID", userID],
          ["Username", user?.userName],
          ["Email", user?.email],
          ["Country", user?.country],
          ["Current account tier", user ? `Tier ${user.level}` : "—"],
        ]}
      />
    </ReviewSection>
  );
}

export function DecisionForm({
  title,
  description,
  rejecting,
  allowReapplication = false,
  pending,
  disabled,
  error,
  onSubmit,
  onCancel,
}: {
  title: string;
  description: string;
  rejecting: boolean;
  allowReapplication?: boolean;
  pending: boolean;
  disabled: boolean;
  error: unknown;
  onSubmit: (reason: string, reapplicationAt?: string) => Promise<unknown>;
  onCancel: () => void;
}) {
  const id = useId();
  const [reason, setReason] = useState("");
  const [reapplicationAt, setReapplicationAt] = useState("");
  const submitting = useRef(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (
      submitting.current ||
      pending ||
      disabled ||
      (rejecting && !reason.trim())
    )
      return;
    submitting.current = true;
    try {
      await onSubmit(
        reason.trim(),
        reapplicationAt ? new Date(reapplicationAt).toISOString() : undefined,
      );
    } catch {
      // The mutation's error is rendered below; retain the input for retry.
    } finally {
      submitting.current = false;
    }
  };
  return (
    <form
      onSubmit={submit}
      aria-label={title}
      className="p-5 mt-5 border border-orange-200 rounded-lg bg-orange-50"
    >
      <h3 className="mb-2 font-medium">{title}</h3>
      <p className="mb-4 text-sm text-gray-600">{description}</p>
      <fieldset disabled={pending || disabled}>
        <label className="block mb-2 text-sm" htmlFor={`${id}-reason`}>
          {rejecting ? "Reason for rejection" : "Review comment (optional)"}
        </label>
        <textarea
          id={`${id}-reason`}
          autoFocus
          required={rejecting}
          className="min-h-[90px] w-full rounded border border-gray-300 p-3 text-sm"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {allowReapplication && rejecting && (
          <div className="mt-4">
            <label htmlFor={`${id}-date`} className="block mb-2 text-sm">
              Reapplication available from (optional, your local time)
            </label>
            <input
              id={`${id}-date`}
              type="datetime-local"
              className="max-w-full p-2 text-sm border border-gray-300 rounded"
              value={reapplicationAt}
              onChange={(event) => setReapplicationAt(event.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave blank to allow immediate resubmission.
            </p>
          </div>
        )}
        <button
          type="submit"
          disabled={pending || disabled || (rejecting && !reason.trim())}
          className={`mt-4 rounded px-4 py-2 text-sm text-white disabled:opacity-50 ${rejecting ? "bg-red-700" : "bg-black"}`}
        >
          {pending
            ? "Saving decision…"
            : rejecting
              ? "Confirm rejection"
              : "Confirm approval"}
        </button>
      </fieldset>
      <button
        type="button"
        disabled={pending}
        className="mt-3 text-sm underline disabled:opacity-50"
        onClick={onCancel}
      >
        Cancel
      </button>
      {Boolean(error) && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {reviewError(error)}
        </p>
      )}
    </form>
  );
}

export function DecisionButtons({
  title,
  disabled,
  choose,
}: {
  title: string;
  disabled: boolean;
  choose: (action: "approve" | "reject") => void;
}) {
  return (
    <div className="flex flex-wrap gap-3 mt-5">
      <button
        type="button"
        disabled={disabled}
        onClick={() => choose("approve")}
        className="px-4 py-2 text-sm text-white bg-black rounded disabled:opacity-50"
      >
        Approve {title}
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => choose("reject")}
        className="px-4 py-2 text-sm text-red-700 border border-red-300 rounded disabled:opacity-50"
      >
        Reject {title}
      </button>
    </div>
  );
}

export function AuditHistory({ logs }: { logs: ReviewAuditLog[] }) {
  return (
    <ReviewSection title="Review history">
      {logs.length ? (
        <ul className="divide-y divide-gray-200">
          {logs.map((log) => (
            <li key={log.id} className="py-3 text-sm">
              <p className="font-medium">{humanize(log.actionType)}</p>
              <p className="mt-1 text-gray-500">
                {log.actor?.displayName || `Admin #${log.adminID}`} ·{" "}
                {dateLabel(log.timestamp)}
              </p>
              {log.reason && (
                <p className="mt-2 break-words whitespace-pre-wrap">
                  {log.reason}
                </p>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">No review decisions yet.</p>
      )}
    </ReviewSection>
  );
}
